import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap, catchError, of, Subject, throwError, map, take, filter, finalize, first } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { AuthEntry } from 'src/app/model/AuthEntry';
import { environment } from 'src/environments/environment';
import { StorageService } from '../storage/storage.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { 
  AUTHENTICATE_ENDPOINT, 
  REFRESH_TOKEN_ENDPOINT, 
  VALIDATE_TOKEN_ENDPOINT, 
  LOGOUT_ENDPOINT 
} from 'src/app/constants/api-routes';

/** Jwt request object */
type JwtRequest = {
  email: string;
  password: string;
};

/** Jwt response object */
type JwtResponse = {
  token: string;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /** Loaded status. Loaded token is set to true when token first loads, and loading subject is set to false when constructor completes */
  public loadedToken: boolean = false;
  private _loadingSubject = new BehaviorSubject<boolean>(true);
  public loading$ = this._loadingSubject.asObservable();

  /** Authenticated status */
  private _isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this._isAuthenticatedSubject.asObservable();

  /** Indicates if a token refresh is in progress */
  private refreshInProgressSubject = new BehaviorSubject<boolean>(false);
  public refreshInProgress$ = this.refreshInProgressSubject.asObservable();

  /** JWT claims subject */
  private claimsSubject = new BehaviorSubject<Map<string, any> | null>(null);
  public claims$ = this.claimsSubject.asObservable();

  /** Whether or not user is logging out. This is used to prevent refreshing on logout. It is updated on login (authenticate) and logout */
  private _loggingOut: boolean = false;

  /** HttpClient instance that bypasses interceptors */
  private httpNoInterceptor: HttpClient;

  /**
   * Constructor initializes services and loads the token from session storage 
   * @param http HttpClient instance for making HTTP requests
   */
  constructor(
    private _storageService: StorageService,
    private httpBackend: HttpBackend
  ) {
    // This HttpClient instance will NOT use interceptors
    this.httpNoInterceptor = new HttpClient(this.httpBackend);

    // Load the token from session storage
    if (!this._loadToken()) {
      this._clearToken();
      this._loadingSubject.next(false);
      return;
    }

    // On successful load, check token validity
    this.validateToken().pipe(
      first(), // Take first event (result)
      tap((isValid: boolean) => {
        if (isValid) {
          // Token is valid according to backend, refresh to reset session
          this.refreshToken().subscribe(() => this._loadingSubject.next(false));
        } else {
          // Token is invalid according to backend, log out
          this._loadingSubject.next(false);
          this.logout();
        }
      }),
      catchError((error) => {
        // On validate error, assume token is invalid
        console.error("[AUTH]: Token validation error", error);
        this._loadingSubject.next(false);
        return of(false);
      }),
    ).subscribe();
  }

  /**
   * Sends login request and stores JWT on success
   * 
   * @param email - User's email
   * @param password - User's password
   * @returns Observable that emits the JWT response
   */
  authenticate(email: string, password: string): Observable<boolean> {
    console.log('[AUTH]: Attempting to log in with email:', email);

    this._loggingOut = false;

    // Create request body
    const body: JwtRequest = { email, password };

    // Post to login endpoint using httpNoInterceptor
    return this.httpNoInterceptor.post<AuthEntry>(AUTHENTICATE_ENDPOINT, body).pipe(
      tap((response: JwtResponse) => this._setToken(response.token)), // Record token
      map(() => true),                                                // Indicate successful authentication
      catchError(this._handleError('Login failed')),                  // Catch errors
    );
  }

  /** 
   * Clears the token and claims and hits the logout endpoint
   */
  logout(): void {
    console.log('[AUTH]: Logging out...');

    this._loggingOut = true;

    this.refreshInProgress$.pipe(
      filter(currentlyRefreshing => !currentlyRefreshing), // Check for non-refreshing state
      take(1),                                             // Unsubscribe after first event
    ).subscribe(() => { // Perform logout
      // Clear token state
      const token = this.token;
      this._clearToken();
      if (!token || token.trim() === '') return;

      // Hit the logout endpoint with the token
      this.httpNoInterceptor.post(LOGOUT_ENDPOINT, token);
    });
  }

  /**
   * Validate that a given token is valid by requesting to the backend
   * 
   * @param token - JWT token to validate
   * @return Observable that emits true if token is valid, false otherwise
   */
  validateToken(): Observable<boolean> {
    // If token is empty, return false immediately
    const token = this.token;
    if (!token || token.trim() === '') {
      return of(false);
    }

    // Hit the validate endpoint with the token
    return this.httpNoInterceptor.post<boolean>(VALIDATE_TOKEN_ENDPOINT, token).pipe(
      take(1),
      tap((isValid: boolean) => this._isAuthenticatedSubject.next(isValid)),
      catchError((error: HttpErrorResponse) => {
        console.error("[AUTH]: Token validation error", error);
        this._isAuthenticatedSubject.next(false);
        return of(false);
      })
    );
  }

  /**
   * Refresh the user's token
   * 
   * @return Observable that emits when the token is refreshed
   */
  refreshToken(): Observable<void> {
    // Don't refresh if logging out
    if (this._loggingOut) return of(void 0);

    console.log("[AUTH]: refreshing user token");
    this.refreshInProgressSubject.next(true);

    // Get the current token
    const token = this.token;

    return this.httpNoInterceptor.post<JwtResponse>(REFRESH_TOKEN_ENDPOINT, token).pipe(
      finalize(() => this.refreshInProgressSubject.next(false)), // On complete, reset refresh state
      tap((resp: JwtResponse) => this._setToken(resp.token)),    // Record new token
      catchError((error: any) => of(void 0)),                    // Handle errors gracefully
      map(() => void 0),                                         // Return completion
    );
  }

  /** 
     * Parses the token from session storage on init 
     * 
     * @return true if token was loaded successfully, false otherwise
     */
  private _loadToken(): boolean | null {
    const token = this._storageService.getItem(AppConstants.KEY_TOKEN);
    if (token) {
      this._parseAndStoreClaims(token);
    }

    this.loadedToken = true;
    return token != undefined;
  }

  /** 
   * Stores the token and parses claims 
   * 
   * @param token - JWT token to be stored
   */
  private _setToken(token: string) {
    this._storageService.setItem(AppConstants.KEY_TOKEN, token);
    this._isAuthenticatedSubject.next(true);
    this._parseAndStoreClaims(token);
  }

  /**
   * Clear token state
   */
  private _clearToken() {
    this._storageService.removeItem(AppConstants.KEY_TOKEN);
    this._isAuthenticatedSubject.next(false);
    this.claimsSubject.next(null);
  }

  /** 
   * Parses JWT and updates BehaviorSubject 
   * 
   * @param token - JWT token to parse
   */
  private _parseAndStoreClaims(token: string) {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      const claims = JSON.parse(decoded);

      this.claimsSubject.next(new Map<string, any>(Object.entries(claims)));
    } catch (e) {
      console.error('[AUTH]: cannot parse JWT claims, invalid format', e);
    }
  }

  /** Returns the raw token */
  get token(): string | null {
    return this._storageService.getItem(AppConstants.KEY_TOKEN);
  }

  /** Get the user's email from the token */
  get email(): string | null {
    const claims = this.claims;
    return claims ? claims.get('sub') || null : null;
  }

  /** Returns claims synchronously */
  get claims(): Map<string, any> | null {
    return this.claimsSubject.value;
  }

  /** Returns whether the user is authenticated */
  get authenticated(): boolean {
    return this._isAuthenticatedSubject.value;
  }

  /** Returns whether or not the token is being refreshed */
  get isRefreshing(): boolean {
    return this.refreshInProgressSubject.value;
  }

  /** Returns whether the token is being loaded */
  get loading(): boolean {
    return this._loadingSubject.value;
  }

  /**
   * Helper that returns a function to handle HTTP errors
   */
  private _handleError(response: string) {
    return (error: HttpErrorResponse) => {
      console.error('[AUTH]: Authentication error', error);

      return throwError(() => {
        if (error.error) {
          return {
            status: error.status,
            error: error.error,
          }
        }

        return new Error(response) || "An error occurred";
      });
    }
  }
}

